/**
 * `connectorAccounts` namespace — English. Declared against the French source
 * type, so a missing or extra key fails the build.
 */

import type { ConnectorAccountsKeys } from './fr';

const connectorAccountsEn: ConnectorAccountsKeys = {
  common: {
    accountsTitle: 'Connected accounts',
    usage: '{{used}} / {{limit}}',
    usageLabel: 'Accounts used: {{used}} of {{limit}}',
    unlimited: '∞',
    default: 'Default',
    overLimit: 'Read-only — above plan limit',
    overLimitHint:
      'This account is above your plan limit: it stays listed but is read-only (no sync). You can still rename or disconnect it.',
    statusConnected: 'Connected',
    statusError: 'Error',
    lastSync: 'Last sync: {{when}}',
    connectedAt: 'Connected {{when}}',
    never: 'never',
    accountFallback: 'Account {{key}}',
    connectAnother: 'Connect another account',
    connectFirst: 'Connect an account',
    setDefault: 'Set as default',
    rename: 'Rename',
    renameLabel: 'Account name',
    save: 'Save',
    cancel: 'Cancel',
    reconnect: 'Reconnect',
    disconnect: 'Disconnect',
    disconnectTitle: 'Disconnect “{{label}}”?',
    disconnectMessage:
      'This {{name}} account will be removed from the workspace. Your other {{name}} accounts stay connected.',
    renamed: 'Account renamed',
    defaultSet: '“{{label}}” is now the default account',
    disconnected: 'Account “{{label}}” disconnected',
    settingsSaved: 'Account settings saved',
    upgradeTitle: 'Account limit reached',
    upgradeHint:
      'Your plan allows {{limit}} {{name}} account(s) ({{used}} in use). Upgrade your plan to connect more.',
    upgradeHintGeneric:
      'Your plan does not allow another {{name}} account. Upgrade your plan to add more.',
    upgradeReadOnly:
      'This account is above your plan limit, so this action is unavailable. Upgrade your plan to re-enable it.',
    upgradeCta: 'View plans',
    planLimitReturn: '{{connector}} connection refused: your plan’s account limit is reached.',
    setupRequired: '{{name}}: platform setup required.',
    addAccountTitle: 'Connect another {{name}} account',
    shopDomainLabel: 'Shopify store domain',
    shopDomainHint: 'The myshopify.com domain of the store to connect.',
    shopDomainInvalid: 'Enter a valid domain ending in .myshopify.com.',
    continue: 'Continue',
    allAccounts: 'All accounts',
    syncOrders: 'Sync orders',
    syncAllAccounts: 'Sync all accounts',
    syncDone: '“{{label}}” sync complete',
    syncAllDone: 'All accounts synced',
    crmPush: 'Push to CRM',
    crmPull: 'Pull from CRM',
    crmPushAll: 'Push (all accounts)',
    crmPullAll: 'Pull (all accounts)',
    crmDone: 'CRM sync complete',
    slackChannel: 'Notification channel',
    slackChannelHint: 'e.g. #announcements — empty = the platform default channel.',
    slackNotify: 'Notify this channel on every publish',
    ga4Property: 'GA4 property',
    ga4SelectProperty: 'Choose a property',
    ga4Loading: 'Loading properties…',
    ga4NoProperties: 'No GA4 property is accessible with this Google account.',
    githubLogin: 'Connected as {{login}}',
    shopLabel: 'Store: {{shop}}',
    storeLabel: 'Store: {{store}}',
    invalidResponse: 'Unexpected server response for this account.',
    shopDomainPlaceholder: 'acme.myshopify.com',
    slackChannelPlaceholder: '#announcements',
    modeLabel: 'Mode: {{mode}}',
  },
  stripe: {
    accountSelect: 'Stripe account',
    usage: 'Stripe accounts: {{used}} / {{limit}}',
    addAccount: 'Connect another Stripe account',
    cancelAdd: 'Cancel',
    labelOptional: 'Account name (optional)',
    labelPlaceholder: 'e.g. EU store',
    syncAll: 'Sync all accounts',
    syncAllResult: '{{count}} account(s) synced · {{conversions}} conversion(s) posted',
    syncAllRow: '{{label}}: {{customers}} customers · {{invoices}} invoices · {{conversions}} conversions',
    syncAllRowError: '{{label}}: error — {{error}}',
    disconnectAccountMessage:
      'Disconnect the Stripe account “{{label}}”? Keep its conversions or purge only this account’s conversions. Your other Stripe accounts stay connected.',
  },
  bip: {
    accountSelect: 'GitHub account',
    addAccount: 'Connect another GitHub account',
    cancelAdd: 'Cancel',
    reposAcross: 'Repositories (all accounts)',
    trackedTitle: 'Tracked repositories for this account',
    trackedNone: 'No tracked repository for this account.',
    untrack: 'Stop tracking {{repo}}',
    useRepo: 'Use {{repo}}',
    trackedSaved: 'Tracked repositories updated',
    generatingFrom: 'Generating from {{repo}} ({{account}})',
  },
};

export default connectorAccountsEn;
